// Environment configuration for the application
// This file determines which API endpoints to use based on the current environment

import { logger } from '../utils/logger';

// Determine if we're in staging or production based on environment variable
// Default to staging if not explicitly set
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'staging';
const IS_PRODUCTION = ENVIRONMENT === 'production';

// API endpoints
const PRODUCTION_API = 'https://chatappaugustsays-g4cvdgf6b7bfbjh7.centralus-01.azurewebsites.net/api/onboarding';
const STAGING_API = 'https://chatappaugustsaysstaging-ccgzgwecewaxcqbr.canadacentral-01.azurewebsites.net/api/onboarding';

const PRODUCTION_CANVASS_API = 'https://canvass-production-gjajfkfbhbagehht.canadacentral-01.azurewebsites.net/api/canvasses';
const STAGING_CANVASS_API = 'https://canvasses-dev-gja8cwabfkgdg2f4.canadacentral-01.azurewebsites.net/api/canvasses';

const PRODUCTION_INTERVIEW_API = 'https://interview-service-production-bncna3fpfqajapfx.canadacentral-01.azurewebsites.net/api';
const STAGING_INTERVIEW_API = 'https://interview-service-staging-fthtb9djb3g6hsc5.canadacentral-01.azurewebsites.net/api';

// Game API endpoints
const PRODUCTION_GAME_API = 'https://chatappaugustsays-g4cvdgf6b7bfbjh7.centralus-01.azurewebsites.net/api/voice';
const STAGING_GAME_API = 'https://chatappaugustsaysstaging-ccgzgwecewaxcqbr.canadacentral-01.azurewebsites.net/api/voice';

const PRODUCTION_CLIENTS_API = 'https://clients-prod-e4bufcf6e3bnhnab.canadacentral-01.azurewebsites.net/api/';
const STAGING_CLIENTS_API = 'https://clients-dev-fmbrdabfexdchca4.canadacentral-01.azurewebsites.net/api/';

const NEWSLETTER_CLIENTS_API = 'https://signup-service-dggwb2fxe4c5gnd3.canadacentral-01.azurewebsites.net/api/newsletter';
const STAGING_NEWSLETTER_CLIENTS_API = 'https://signup-service-staging-fmd4gjcnhvgwesbq.canadacentral-01.azurewebsites.net/api/newsletter';

const AUTOMATE_SERVICE_API = 'https://automate-service-aehqfmgpfcaxeshe.canadacentral-01.azurewebsites.net/api/automate/interview';

const PRODUCTION_DISCOVERY_API_BASE = 'https://discovery-service-production-fserceexamdac5en.canadacentral-01.azurewebsites.net/api/discovery';
const STAGING_DISCOVERY_API_BASE = 'https://discovery-service-stage-arfwafhggdgvf8ds.canadacentral-01.azurewebsites.net/api/discovery';

const PRODUCTION_REPORT_API_BASE = 'https://report-api-prod1-dfeyamfjhcfwe3cg.canadacentral-01.azurewebsites.net/api';
const STAGING_REPORT_API_BASE = 'https://report-api-dev-bne6ctgpcqdbftd0.canadacentral-01.azurewebsites.net/api';

const PRODUCTION_ANALYTICS_API_BASE = 'https://analytics-service-prod-d3hgd7h4e4dragb0.canadacentral-01.azurewebsites.net/api';
const STAGING_ANALYTICS_API_BASE = 'https://analytics-service-staging-cmfycgdqhrgxejfq.canadacentral-01.azurewebsites.net/api';

// Get current frontend URL dynamically
const getCurrentFrontendUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback for SSR or build time
  return '';
};

// Export configuration
export const config = {
  environment: ENVIRONMENT,
  isProduction: IS_PRODUCTION,
  isStaging: !IS_PRODUCTION,
  
  // API endpoint - can be overridden by env variable
  copilotRuntimeUrl: process.env.REACT_APP_COPILOT_RUNTIME_URL || (IS_PRODUCTION ? PRODUCTION_API : STAGING_API),
  
  // Game API endpoint - can be overridden by env variable
  copilotGameRuntimeUrl: process.env.REACT_APP_COPILOT_GAME_RUNTIME_URL || (IS_PRODUCTION ? PRODUCTION_GAME_API : STAGING_GAME_API),

  canvassApiBase: process.env.REACT_APP_CANVASS_API_BASE || (IS_PRODUCTION ? PRODUCTION_CANVASS_API : STAGING_CANVASS_API),
  interviewApiBase: process.env.REACT_APP_INTERVIEW_API_BASE || (IS_PRODUCTION ? PRODUCTION_INTERVIEW_API : STAGING_INTERVIEW_API),

  clientsApiBase: process.env.REACT_APP_CLIENTS_API_BASE || (IS_PRODUCTION ? PRODUCTION_CLIENTS_API : STAGING_CLIENTS_API),

  newsletterApiBase: process.env.REACT_APP_NEWSLETTER_API_BASE || (IS_PRODUCTION ? NEWSLETTER_CLIENTS_API : STAGING_NEWSLETTER_CLIENTS_API),

  automateServiceBase: process.env.REACT_APP_AUTOMATE_SERVICE_API || AUTOMATE_SERVICE_API,

  discoveryApiBase: process.env.REACT_APP_DISCOVERY_API_BASE || (IS_PRODUCTION ? PRODUCTION_DISCOVERY_API_BASE : STAGING_DISCOVERY_API_BASE),

  reportApiBase: process.env.REACT_APP_REPORT_API_BASE || (IS_PRODUCTION ? PRODUCTION_REPORT_API_BASE : STAGING_REPORT_API_BASE),

  analyticsServiceBase: process.env.REACT_APP_ANALYTICS_API_BASE || (IS_PRODUCTION ? PRODUCTION_ANALYTICS_API_BASE : STAGING_ANALYTICS_API_BASE),

  analyticsFunctionKey: process.env.REACT_APP_ANALYTICS_FUNCTION_KEY || 'test-dev-key-12345',
  
  // Frontend URL - dynamically determined from current location
  get frontendUrl() {
    return getCurrentFrontendUrl();
  },
  
  // Helper function to generate game URL
  getGameUrl: (conversationId) => {
    const baseUrl = getCurrentFrontendUrl();
    return `${baseUrl}/interview/${conversationId}`;
  },
  
  // Helper function to log current environment
  logEnvironment: () => {
    logger.log(`Environment: ${config.environment}`);
    logger.log(`Copilot API: ${config.copilotRuntimeUrl}`);
    logger.log(`Copilot Game API: ${config.copilotGameRuntimeUrl}`);
    logger.log(`Frontend URL: ${config.frontendUrl}`);
    logger.log(`REACT_APP_ENVIRONMENT: ${process.env.REACT_APP_ENVIRONMENT || 'not set (defaulting to staging)'}`);
  }
};

// Log environment on load
logger.log(`Running in ${config.environment} mode`);
logger.log(`API: ${config.copilotRuntimeUrl}`);
logger.log(`REACT_APP_ENVIRONMENT: ${process.env.REACT_APP_ENVIRONMENT || 'not set (defaulting to staging)'}`);
